import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Image, Upload } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import type { ProductionQuestionAttachment } from '../../interfaces/ProductionQuestion.interface';

export interface ExistingAttachment extends ProductionQuestionAttachment {}

interface ImagePasteUploadProps {
	label: string;
	files: File[];
	onFilesChange: (files: File[]) => void;
	existingAttachments?: ExistingAttachment[];
	onDeleteExisting?: (attachmentId: string) => void;
	disabled?: boolean;
	deletingAttachmentId?: string | null;
}

const ImagePasteUpload: React.FC<ImagePasteUploadProps> = ({
	label,
	files,
	onFilesChange,
	existingAttachments = [],
	onDeleteExisting,
	disabled = false,
	deletingAttachmentId = null,
}) => {
	const zoneRef = useRef<HTMLDivElement>(null);
	const [focused, setFocused] = useState(false);
	const [previewUrls, setPreviewUrls] = useState<string[]>([]);

	useEffect(() => {
		const urls = files.map((file) => URL.createObjectURL(file));
		setPreviewUrls(urls);
		return () => {
			urls.forEach((url) => URL.revokeObjectURL(url));
		};
	}, [files]);

	const appendFiles = useCallback(
		(incoming: File[]) => {
			const images = incoming.filter((file) => file.type.startsWith('image/'));
			if (!images.length) return;
			onFilesChange([...files, ...images]);
		},
		[files, onFilesChange]
	);

	const handlePaste = useCallback(
		(event: React.ClipboardEvent) => {
			if (disabled) return;
			const items = event.clipboardData?.items;
			if (!items) return;

			const pasted: File[] = [];
			for (const item of items) {
				if (item.type.startsWith('image/')) {
					const file = item.getAsFile();
					if (file) pasted.push(file);
				}
			}
			if (pasted.length) {
				event.preventDefault();
				appendFiles(pasted);
			}
		},
		[appendFiles, disabled]
	);

	const removePending = (index: number) => {
		onFilesChange(files.filter((_, i) => i !== index));
	};

	return (
		<div className="flex flex-col gap-2">
			<span className="text-sm font-medium">{label}</span>
			<div
				ref={zoneRef}
				tabIndex={0}
				onFocus={() => setFocused(true)}
				onBlur={() => setFocused(false)}
				onPaste={handlePaste}
				className={`rounded border border-dashed p-3 outline-none transition-colors ${
					focused ? 'border-blue-400 bg-blue-50/40' : 'border-neutral-300'
				}`}
			>
				<div className="mb-2 text-xs text-neutral-500">
					Click to upload, drag files here, or paste screenshots while this box
					is focused.
				</div>

				{!disabled && (
					<Upload
						listType="picture-card"
						showUploadList={false}
						beforeUpload={(file) => {
							appendFiles([file]);
							return false;
						}}
						multiple
						accept="image/*"
						disabled={disabled}
						className="[&_.ant-upload-select]:!m-0"
					>
						<div>
							<PlusOutlined />
							<div className="mt-1">Upload</div>
						</div>
					</Upload>
				)}

				{files.length > 0 && (
					<div className="mt-3 flex flex-wrap gap-3">
						<Image.PreviewGroup>
							{files.map((file, index) => (
								<div
									key={`${file.name}-${index}`}
									className="flex w-[96px] flex-col items-center gap-2"
								>
									<Image
										src={previewUrls[index]}
										alt={file.name}
										width={96}
										height={96}
										className="cursor-pointer rounded object-cover"
									/>
									{!disabled && (
										<Button
											type="text"
											size="small"
											danger
											icon={<DeleteOutlined />}
											onClick={() => removePending(index)}
											className="h-auto px-1 py-0 text-xs"
										>
											Remove
										</Button>
									)}
								</div>
							))}
						</Image.PreviewGroup>
					</div>
				)}

				{existingAttachments.length > 0 && (
					<div className="mt-3 flex flex-wrap gap-3">
						<Image.PreviewGroup>
							{existingAttachments.map((attachment) => (
								<div
									key={attachment.id}
									className="flex w-[96px] flex-col items-center gap-2"
								>
									<Image
										src={attachment.url ?? undefined}
										alt={label}
										width={96}
										height={96}
										className="cursor-pointer rounded object-cover"
									/>
									{onDeleteExisting && !disabled && (
										<Button
											type="text"
											size="small"
											danger
											icon={<DeleteOutlined />}
											loading={deletingAttachmentId === attachment.id}
											onClick={() => onDeleteExisting(attachment.id)}
											className="h-auto px-1 py-0 text-xs"
										>
											Remove
										</Button>
									)}
								</div>
							))}
						</Image.PreviewGroup>
					</div>
				)}
			</div>
		</div>
	);
};

export default ImagePasteUpload;
